import {
  type CpComparison,
  type CpPeriodValues,
  cpVerdict,
  type SpeedLeg,
  speedLegs,
} from '@felix/domain';
import { Skeleton, Table } from '@mantine/core';
import {
  IconCalendarTime,
  IconChartBar,
  IconClock,
  IconFileDescription,
  IconGauge,
  IconRoute,
  IconRouteSquare,
  IconRuler2,
  IconShip,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { ScrollTable } from './fields.js';
import shared from './NoonReportScreen.module.css';
import { formatStamp } from './NoonSections.js';
import { useNumber } from './numbers.js';
import { formatDuration } from './overview.js';
import classes from './PerformanceScreens.module.css';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyagePerformanceView } from './types.js';
import { type FrameData, VoyageFrame } from './VoyageFrame.js';
import { SummaryTile } from './VoyageOverviewScreen.js';
import { useReferenceLabel } from './VoyageReference.js';

type CpView = NonNullable<VoyagePerformanceView['performance']['cp']>;
type CpWindowView = NonNullable<CpView['windows']>[number];

/** CP hızı: yük durumuna göre (laden / ballast); bilinmiyorsa laden, o da yoksa ballast. */
function plannedSpeedOf(voyage: Voyage, view: VoyagePerformanceView | null): number | null {
  if (view?.cp?.plannedSpeedKn !== undefined && view.cp.plannedSpeedKn !== null) {
    return view.cp.plannedSpeedKn;
  }
  const load = view?.performance.cp?.loadCondition;
  // Referans (ADR 0084 Ek 2): CP > Owner's reference; eski sunucuda seferin CP kolonları
  const ref = view?.reference;
  const laden = ref ? ref.speedLadenKn : voyage.cpSpeedLadenKn;
  const ballast = ref ? ref.speedBallastKn : voyage.cpSpeedBallastKn;
  if (load === 'ballast') return ballast;
  return laden ?? ballast;
}

/** CP pencereleri: sunucu ayrı pencereleri verir; eski yanıtta tek pencere (COSP → EOSP). */
function windowsOf(cp: CpView | null): CpWindowView[] {
  if (!cp) return [];
  return (
    cp.windows ?? [
      {
        start: cp.start,
        end: cp.end,
        windowHours: cp.windowHours,
        excludedHours: cp.excludedHours,
        hours: cp.hours,
        distanceNm: cp.distanceNm,
        avgSpeedKn: cp.avgSpeedKn,
      },
    ]
  );
}

/** Rapor bir CP penceresinin içinde mi (COSP sonrası, EOSP dahil; G4-69). */
const inWindow = (at: string, windows: readonly CpWindowView[]) =>
  windows.some(
    (w) =>
      Date.parse(at) > Date.parse(w.start) &&
      (w.end === null || Date.parse(at) <= Date.parse(w.end)),
  );

/** Rapor rapor hızın CP ile durumu: grafik ve tablo aynı şeyi söyler (G4-69, G4-72). */
type LegCp = 'in' | 'below' | 'outside' | 'none';
function legCp(leg: SpeedLeg, windows: readonly CpWindowView[], cpSpeed: number | null): LegCp {
  if (leg.avgSpeedKn === null) return 'none';
  if (!inWindow(leg.reportedAt, windows)) return 'outside';
  if (cpSpeed === null) return 'none';
  return leg.avgSpeedKn >= cpSpeed ? 'in' : 'below';
}

const LEG_TONE: Record<Exclude<LegCp, 'none'>, StatusTone> = {
  in: 'success',
  below: 'warning',
  outside: 'neutral',
};

export interface PerformanceScreenProps {
  source: VoyageSource;
}

/** Performans ekranlarının başlık kartı: sefer no, başlangıç, bitiş ve CP (G4-03). */
function useHeadFacts() {
  const { t } = useTranslation();
  const referenceLabel = useReferenceLabel();
  return (voyage: Voyage, view?: VoyagePerformanceView | null) => [
    { label: t('voyage.noon.header.voyageNo'), value: voyage.voyageNo },
    {
      label: t('voyage.performance.startedUtc'),
      value: voyage.start ? formatStamp(voyage.start) : '—',
    },
    { label: t('voyage.performance.endedUtc'), value: voyage.end ? formatStamp(voyage.end) : '—' },
    // Karşılaştırmanın referansı: CP no, "Owner's reference" ya da yok (ADR 0084 Ek 2)
    view?.reference
      ? {
          label: t('voyage.cpReference.reference'),
          // Başlık kartında kısa: CP no; değilse "Owner's reference" ya da "No reference"
          value:
            view.reference.kind === 'charter_party'
              ? (view.reference.cpNo ?? view.reference.charterer ?? referenceLabel(view.reference))
              : referenceLabel(view.reference),
        }
      : { label: t('voyage.noon.voyageTab.charterParty'), value: voyage.charterPartyNo ?? '—' },
  ];
}

/**
 * Voyage Performance (menü "Voyage › Voyage Performance"; ADR 0022 §4; M6f G4-64…G4-75). Seferin
 * tamamı (Overall), CP pencereleri (her COSP → EOSP ayrı ve toplam) ve boğaz/kanal segmentleri;
 * rapor rapor hız dökümü. Görsel referansı yok; dil `gemi-sefer-yonetimi.jpeg` ile aynı.
 */
export function VoyagePerformanceScreen({ source }: PerformanceScreenProps) {
  const { t } = useTranslation();
  const facts = useHeadFacts();
  const k = (key: string) => t(`voyage.performance.${key}`);
  return (
    <VoyageFrame
      source={source}
      title={k('title')}
      description={k('description')}
      facts={facts}
      skeleton={
        <>
          <Skeleton height={96} radius="md" />
          <div className={classes.pair}>
            <Skeleton height={300} radius="md" />
            <Skeleton height={300} radius="md" />
          </div>
          <Skeleton height={360} radius="md" />
        </>
      }
    >
      {(data) => <VoyagePerformanceBody data={data} />}
    </VoyageFrame>
  );
}

function VoyagePerformanceBody({ data }: { data: FrameData }) {
  const { t } = useTranslation();
  const referenceLabel = useReferenceLabel();
  const n = useNumber();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.performance.${key}`, options ?? {});
  const { voyage, reports, performance: view } = data;
  const overall = view?.performance.overall ?? null;
  const cp = view?.performance.cp ?? null;
  const windows = windowsOf(cp);
  const segments = view?.performance.segments ?? [];
  const legs = speedLegs(reports);
  const cpSpeed = plannedSpeedOf(voyage, view);
  const one = { digits: 1, fixed: true } as const;
  const hours = (v: number | null | undefined) => n(v ?? null, one);
  const onTarget = cp?.avgSpeedKn != null && cpSpeed !== null && cp.avgSpeedKn >= cpSpeed;

  const rows: {
    key: string;
    name: string;
    start: string | null;
    end: string | null;
    h: number | null;
    d: number | null;
    s: number | null;
    note: string;
    total?: boolean;
    sub?: boolean;
  }[] = [];
  if (overall) {
    rows.push({
      key: 'overall',
      name: k('overall'),
      start: overall.start,
      end: overall.end,
      h: overall.hours,
      d: overall.distanceNm,
      s: overall.avgSpeedKn,
      note: k('allIncluded'),
    });
  }
  windows.forEach((w, i) => {
    rows.push({
      key: `cp-${i}`,
      name: windows.length > 1 ? k('cpWindowN', { n: i + 1 }) : k('cpWindow'),
      start: w.start,
      end: w.end,
      h: w.hours,
      d: w.distanceNm,
      s: w.avgSpeedKn,
      note:
        w.excludedHours > 0 ? k('excluded', { value: n(w.excludedHours, one) }) : k('noExclusions'),
      sub: windows.length > 1,
    });
  });
  if (cp && windows.length > 1) {
    rows.push({
      key: 'cp-total',
      name: k('cpTotal'),
      start: cp.start,
      end: cp.end,
      h: cp.hours,
      d: cp.distanceNm,
      s: cp.avgSpeedKn,
      note:
        cp.excludedHours > 0
          ? k('excluded', { value: n(cp.excludedHours, one) })
          : k('noExclusions'),
      total: true,
    });
  }
  segments.forEach((segment, i) => {
    rows.push({
      key: `segment-${i}`,
      name: `${k(segment.type)} · ${segment.name}`,
      start: segment.start,
      end: segment.end,
      h: segment.hours,
      d: segment.distanceNm,
      s: segment.avgSpeedKn,
      note: k('notInCp'),
    });
  });

  return (
    <>
      <div className={classes.tiles}>
        <SummaryTile
          icon={<IconRuler2 size={24} />}
          label={k('overallDistance')}
          value={n(overall?.distanceNm ?? null, one)}
          unit="NM"
        />
        <SummaryTile
          icon={<IconClock size={24} />}
          label={k('overallTime')}
          value={formatDuration(overall?.hours)}
        />
        <SummaryTile
          icon={<IconGauge size={24} />}
          label={k('overallSpeed')}
          value={n(overall?.avgSpeedKn ?? null, one)}
          unit="kn"
        />
        <SummaryTile
          icon={<IconRouteSquare size={24} />}
          label={k('cpDistance')}
          value={n(cp?.distanceNm ?? null, one)}
          unit="NM"
        />
        <SummaryTile
          icon={<IconCalendarTime size={24} />}
          label={k('cpTime')}
          value={formatDuration(cp?.hours)}
        />
        <SummaryTile
          icon={<IconShip size={24} />}
          label={k('cpSpeed')}
          value={n(cp?.avgSpeedKn ?? null, one)}
          unit="kn"
          tone={onTarget ? 'success' : undefined}
        />
      </div>
      <div className={classes.pair}>
        <Panel
          title={k('segments')}
          icon={<IconRoute size={20} />}
          divider
          className={classes.panel}
        >
          {rows.length === 0 ? (
            <EmptyState icon={<IconRoute size={22} stroke={1.8} />} title={k('noSegments')} />
          ) : (
            <div className={classes.panelBody}>
              <ScrollTable label={k('segmentsTable')}>
                <Table className={[shared.table, classes.segments].join(' ')} withColumnBorders>
                  <colgroup>
                    <col className={classes.colSegment} />
                    <col className={classes.colPeriod} />
                    <col className={classes.colNum} />
                    <col className={classes.colNum} />
                    <col className={classes.colNum} />
                    <col />
                  </colgroup>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{k('segment')}</Table.Th>
                      <Table.Th>{k('period')}</Table.Th>
                      <Table.Th data-numeric>{k('hours')}</Table.Th>
                      <Table.Th data-numeric>{t('voyage.overview.distance')}</Table.Th>
                      <Table.Th data-numeric>{k('avgSpeed')}</Table.Th>
                      <Table.Th>{k('cpRule')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((row) => (
                      <Table.Tr key={row.key} data-total={row.total || undefined}>
                        <Table.Td className={classes.segmentName} data-sub={row.sub || undefined}>
                          {row.name}
                        </Table.Td>
                        <Table.Td className={classes.period}>
                          <span>{formatStamp(row.start)}</span>
                          <span>{`→ ${row.end ? formatStamp(row.end) : k('ongoing')}`}</span>
                        </Table.Td>
                        <Table.Td data-numeric>{hours(row.h)}</Table.Td>
                        <Table.Td data-numeric>{n(row.d, one)}</Table.Td>
                        <Table.Td data-numeric>{n(row.s, one)}</Table.Td>
                        <Table.Td className={classes.note}>{row.note}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollTable>
              <p className={classes.ruleNote}>{k('rules')}</p>
              {view?.reference ? (
                <p className={classes.ruleNote}>
                  {`${t('voyage.cpReference.reference')}: ${referenceLabel(view.reference)}. ${
                    view.reference.kind === 'none'
                      ? t('voyage.cpReference.noReference')
                      : t('voyage.cpReference.indicativeNote')
                  }`}
                </p>
              ) : null}
            </div>
          )}
        </Panel>
        <Panel
          title={k('speedByReport')}
          icon={<IconChartBar size={20} />}
          divider
          className={classes.panel}
        >
          <SpeedChart legs={legs} windows={windows} cpSpeed={cpSpeed} />
        </Panel>
      </div>
      <Panel title={k('legs')} icon={<IconFileDescription size={20} />} divider>
        {legs.length === 0 ? (
          <EmptyState
            icon={<IconFileDescription size={22} stroke={1.8} />}
            title={t('voyage.overview.noReports')}
          />
        ) : (
          <ScrollTable label={k('legsTable')}>
            <Table className={[shared.table, classes.legs].join(' ')} withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('voyage.overview.date')}</Table.Th>
                  <Table.Th>{t('voyage.overview.reportType')}</Table.Th>
                  <Table.Th>{t('voyage.overview.timeUtc')}</Table.Th>
                  <Table.Th data-numeric>{k('hoursSince')}</Table.Th>
                  <Table.Th data-numeric>{t('voyage.overview.distance')}</Table.Th>
                  <Table.Th data-numeric>{k('avgSpeed')}</Table.Th>
                  <Table.Th>{k('vsCp')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {legs.map((leg) => {
                  const stamp = formatStamp(leg.reportedAt);
                  const status = legCp(leg, windows, cpSpeed);
                  return (
                    <Table.Tr key={leg.id}>
                      <Table.Td>{stamp.slice(0, 10)}</Table.Td>
                      <Table.Td>{t(`voyage.kinds.${leg.kind}`)}</Table.Td>
                      <Table.Td>{stamp.slice(11)}</Table.Td>
                      <Table.Td data-numeric>{hours(leg.hours)}</Table.Td>
                      <Table.Td data-numeric>{n(leg.distanceNm, one)}</Table.Td>
                      <Table.Td data-numeric>{n(leg.avgSpeedKn, one)}</Table.Td>
                      <Table.Td>
                        {status === 'none' ? (
                          '—'
                        ) : (
                          <StatusBadge tone={LEG_TONE[status]} withIcon={status !== 'outside'}>
                            {k(`legCp.${status}`)}
                          </StatusBadge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollTable>
        )}
      </Panel>
    </>
  );
}

/** Grafiğin genişliği: viewBox gerçek genişliğe eşitlenir, yazı 11–12 px'te sabit kalır (G4-71). */
function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * Rapor rapor hız çubukları (G4-66, G4-69, G4-70, G4-71, G4-75): CP penceresi içindekiler CP hızıyla
 * karşılaştırılır (altı amber), dışındakiler nötr; CP hızı kesikli çizgi, etiketlerden önce çizilir.
 * Değerler tabloda da var.
 */
function SpeedChart({
  legs,
  windows,
  cpSpeed,
}: {
  legs: readonly SpeedLeg[];
  windows: readonly CpWindowView[];
  cpSpeed: number | null;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.performance.${key}`, options ?? {});
  const [ref, width] = useWidth<HTMLDivElement>(560);
  const short = typeof window !== 'undefined' && window.innerHeight < 800;
  const bars = legs.filter((leg) => leg.avgSpeedKn !== null);
  if (bars.length === 0) {
    return <EmptyState icon={<IconChartBar size={22} stroke={1.8} />} title={k('noSpeeds')} />;
  }
  // Eksen onun katına yuvarlanır: 0, yarısı ve üstü tam sayı
  const max = Math.max(
    10,
    Math.ceil((Math.max(cpSpeed ?? 0, ...bars.map((b) => b.avgSpeedKn as number)) * 1.1) / 10) * 10,
  );
  const height = short ? 220 : 250;
  const left = 34;
  const right = 8;
  const top = 18;
  const bottom = height - 34;
  const plot = bottom - top;
  const step = (width - left - right) / bars.length;
  const barWidth = Math.max(6, Math.min(step * 0.6, 44));
  const y = (value: number) => bottom - (value / max) * plot;
  const days = bars.map((bar) => formatStamp(bar.reportedAt).slice(0, 5));
  const statuses = bars.map((bar) => legCp(bar, windows, cpSpeed));
  const outside = statuses.includes('outside');
  const below = statuses.filter((s) => s === 'below').length;
  const summary = k('chartSummary', {
    count: bars.length,
    min: n(Math.min(...bars.map((b) => b.avgSpeedKn as number)), 1),
    max: n(Math.max(...bars.map((b) => b.avgSpeedKn as number)), 1),
    cp: cpSpeed === null ? '—' : n(cpSpeed, 1),
    below,
  });
  return (
    <figure className={classes.chart}>
      <div ref={ref} className={classes.chartBox}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          role="img"
          aria-label={summary}
        >
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line
                x1={left}
                x2={width - right}
                y1={y(max * f)}
                y2={y(max * f)}
                className={classes.gridLine}
              />
              <text x={left - 6} y={y(max * f) + 4} textAnchor="end" className={classes.axis}>
                {n(max * f, 0)}
              </text>
            </g>
          ))}
          {cpSpeed !== null ? (
            <line
              x1={left}
              x2={width - right}
              y1={y(cpSpeed)}
              y2={y(cpSpeed)}
              className={classes.cpLine}
              strokeDasharray="6 4"
            />
          ) : null}
          {bars.map((bar, i) => {
            const value = bar.avgSpeedKn as number;
            const status = statuses[i];
            const x = left + i * step + (step - barWidth) / 2;
            const stamp = formatStamp(bar.reportedAt);
            // Değer CP çizgisine yakınsa etiket çubuğun içinde (G4-66)
            const inside =
              cpSpeed !== null && Math.abs(value - cpSpeed) < 1.2 && bottom - y(value) > 22;
            const repeated = days[i] === days[i - 1] || days[i] === days[i + 1];
            return (
              <g key={bar.id}>
                <title>
                  {`${stamp} UTC · ${t(`voyage.kinds.${bar.kind}`)} · ${n(bar.distanceNm, 1)} NM · ${n(bar.hours, 1)} h · ${n(value, 2)} kn`}
                </title>
                <rect
                  x={x}
                  y={y(value)}
                  width={barWidth}
                  height={bottom - y(value)}
                  rx="3"
                  className={classes.bar}
                  data-tone={status}
                />
                <text
                  x={x + barWidth / 2}
                  y={inside ? y(value) + 14 : y(value) - 5}
                  textAnchor="middle"
                  className={classes.barLabel}
                  data-inside={inside || undefined}
                >
                  {n(value, 1)}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={bottom + 14}
                  textAnchor="middle"
                  className={classes.axis}
                >
                  {days[i]}
                </text>
                {repeated ? (
                  <text
                    x={x + barWidth / 2}
                    y={bottom + 27}
                    textAnchor="middle"
                    className={classes.axisMinor}
                  >
                    {stamp.slice(11)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>
      <figcaption className={classes.legend}>
        <span data-tone="speed">{k('avgSpeedLegend')}</span>
        {below > 0 ? <span data-tone="below">{k('belowCpLegend')}</span> : null}
        {outside ? <span data-tone="outside">{k('outsideLegend')}</span> : null}
        {cpSpeed !== null ? (
          <span data-tone="cp">{k('cpSpeedLegend', { value: n(cpSpeed, 1) })}</span>
        ) : null}
      </figcaption>
    </figure>
  );
}

/** Dört KPI hücresi: planlanan ve gerçek hız, planlanan ve gerçek tüketim; fark çipleri (G4-77). */
export function CpKpis({
  comparison,
  plannedSpeed,
}: {
  comparison: CpComparison | null;
  plannedSpeed: number | null;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.cp.${key}`);
  const one = { digits: 1, fixed: true } as const;
  const chip = (value: number | null, unit: string, goodWhenPositive: boolean) =>
    value === null ? null : (
      <StatusBadge tone={value >= 0 === goodWhenPositive ? 'success' : 'danger'}>
        {`${value > 0 ? '+' : ''}${n.unit(value, unit, one)}`}
      </StatusBadge>
    );
  return (
    <dl className={shared.cpGrid}>
      <div>
        <dt className={shared.itemLabel}>{k('plannedSpeed')}</dt>
        <dd>
          <strong>{n.unit(comparison?.plannedSpeedKn ?? plannedSpeed, 'kn', one)}</strong>
        </dd>
      </div>
      <div>
        <dt className={shared.itemLabel}>{k('actualSpeed')}</dt>
        <dd>
          <strong>{n.unit(comparison?.actualSpeedKn ?? null, 'kn', one)}</strong>
          {chip(comparison?.speedDiffKn ?? null, 'kn', true)}
        </dd>
      </div>
      <div>
        <dt className={shared.itemLabel}>{k('plannedConsumption')}</dt>
        <dd>
          <strong>{n.unit(comparison?.plannedConsumptionMt ?? null, 'MT/day', one)}</strong>
        </dd>
      </div>
      <div>
        <dt className={shared.itemLabel}>{k('actualConsumption')}</dt>
        <dd>
          <strong>{n.unit(comparison?.actualConsumptionMt ?? null, 'MT/day', one)}</strong>
          {chip(comparison?.consumptionDiffMt ?? null, 'MT', false)}
        </dd>
      </div>
    </dl>
  );
}

export function Facts({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className={classes.facts}>
      {rows.map(([label, value], index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: sabit satır listesi
        <div key={index}>
          <dt>{label}</dt>
          <dd title={typeof value === 'string' ? value : undefined}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Bir dönemin CP karşılaştırması: ortalama hız ve günlük tüketim, plan, gerçek ve fark çipi;
 * iki tabloda ortak sütun genişlikleri (G4-79). Yük durumu bilinmiyorsa planlı hız ekranın
 * varsayımından, tüketim boş; eksik değer çiftleri tek "—" (G4-83).
 */
export function CompareTable({
  label,
  values,
  plannedSpeed,
}: {
  label: string;
  values: CpPeriodValues | null;
  plannedSpeed: number | null;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const one = { digits: 1, fixed: true } as const;
  const cp = values?.comparison ?? null;
  const verdict = cpVerdict(cp);
  const chip = (value: number | null | undefined, unit: string, ok: boolean | null) =>
    value === null || value === undefined ? (
      '—'
    ) : (
      <StatusBadge tone={ok ? 'success' : 'warning'} withIcon>
        {`${value > 0 ? '+' : ''}${n.unit(value, unit, one)}`}
      </StatusBadge>
    );
  return (
    <ScrollTable label={label}>
      <Table className={[shared.table, classes.compare].join(' ')} withColumnBorders>
        <colgroup>
          <col className={classes.colParam} />
          <col className={classes.colValue} />
          <col className={classes.colValue} />
          <col className={classes.colValue} />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('voyage.overview.parameter')}</Table.Th>
            <Table.Th data-numeric>{t('voyage.overview.planned')}</Table.Th>
            <Table.Th data-numeric>{t('voyage.overview.actual')}</Table.Th>
            <Table.Th>{t('voyage.overview.difference')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>{t('voyage.overview.avgSpeed')}</Table.Td>
            <Table.Td data-numeric>
              {n.unit(cp?.plannedSpeedKn ?? plannedSpeed, 'kn', one)}
            </Table.Td>
            <Table.Td data-numeric>{n.unit(values?.avgSpeedKn ?? null, 'kn', one)}</Table.Td>
            <Table.Td>{chip(cp?.speedDiffKn, 'kn', verdict.speed)}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td>{t('voyage.charterParty.dailyConsumption')}</Table.Td>
            <Table.Td data-numeric>{n.unit(cp?.plannedConsumptionMt ?? null, 'MT', one)}</Table.Td>
            <Table.Td data-numeric>
              {n.unit(values?.dailyConsumptionMt ?? null, 'MT', one)}
            </Table.Td>
            <Table.Td>{chip(cp?.consumptionDiffMt, 'MT', verdict.consumption)}</Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </ScrollTable>
  );
}
